import { useEffect, useState, useRef, useCallback } from 'react';
import Footer from '../../components/Footer/Footer';
import Header from '../../components/Header/Header';
import NavInf from '../../components/NavInf/NavInf';
import ProductWrapper from '../../components/Product/ProductWrapper';
import ProductTable from '../../components/ProductTable/ProductTable';
import Masonry from 'react-masonry-css';
import { ClipLoader } from 'react-spinners';
import throttle from 'lodash.throttle';
import './MyProducts.css';
import { useNavigate } from 'react-router-dom';
import { faPlus, faGrip, faList, faEllipsisV } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../config/firebase';
import ShareButton from '../../components/ShareButton/ShareButton';
import { isEmbedded } from '../../utils/isEmbedded';
import EmbeddedLayout from '../../components/EmbeddedLayout/EmbeddedLayout';
import { getLoginRoute } from '../../utils/navigationHelper';
import ProductBulkImportModal from '../../components/ProductBulkImportModal/ProductBulkImportModal';
import ProductBulkEditModal from '../../components/ProductBulkEditModal/ProductBulkEditModal';

const ITEMS_PER_LOAD = 10;

function MyProducts() {
  const navigate = useNavigate();
  const [productos, setProductos] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [visibleProducts, setVisibleProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadIndexRef = useRef(0);
  const [hasMore, setHasMore] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  // eslint-disable-next-line no-unused-vars
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showBulkMenu, setShowBulkMenu] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const bulkMenuRef = useRef(null);
  const user = JSON.parse(localStorage.getItem('usuario'));
  const embedded = isEmbedded();

  const breakpointColumnsObj = {
    default: 6,
    1600: 6,
    1200: 5,
    768: 3,
    480: 2
  };

  const loadMore = useCallback(() => {
    if (loadingMore || !hasMore || viewMode === 'table') return;
    setLoadingMore(true);

    setTimeout(() => {
      const next = filteredProducts.slice(
        loadIndexRef.current,
        loadIndexRef.current + ITEMS_PER_LOAD
      );
      setVisibleProducts(prev => [...prev, ...next]);
      loadIndexRef.current += ITEMS_PER_LOAD;
      if (loadIndexRef.current >= filteredProducts.length) {
        setHasMore(false);
      }
      setLoadingMore(false);
    }, 500);
  }, [filteredProducts, loadingMore, hasMore, viewMode]);

  const handleScroll = useCallback(() => {
    if (loading || loadingMore || !hasMore || viewMode === 'table') return;

    if (
      window.innerHeight +
      document.documentElement.scrollTop +
      200 >=
      document.documentElement.offsetHeight
    ) {
      loadMore();
    }
  }, [loading, loadingMore, hasMore, loadMore, viewMode]);

  // Fetch categorías del proveedor (solo en modo embebido)
  useEffect(() => {
    if (!embedded) return;
    
    const fetchCategories = async () => {
      try {
        const user = JSON.parse(localStorage.getItem('usuario'));
        if (!user?.proveedorInfo?.id) return;

        const res = await fetch(`https://api.minymol.com/providers/${user.proveedorInfo.id}/categories-or-subcategories`);
        if (res.ok) {
          const data = await res.json();
          setCategories(data.items || []);
        }
      } catch (err) {
        console.error("Error al cargar categorías:", err);
      }
    };

    fetchCategories();
  }, [embedded]);

  useEffect(() => {
    const fetchProductos = async () => {
      try {
        setLoading(true);
        setVisibleProducts([]);
        loadIndexRef.current = 0;
        setHasMore(true);

        const user = JSON.parse(localStorage.getItem('usuario'));
        if (!user?.proveedorInfo?.id) {
          alert('Solo los proveedores pueden ver sus productos.');
          return;
        }

        const lastFetch = window._myProductsFetch || 0;
        const now = Date.now();
        if (now - lastFetch < 12000) {
          setLoading(false);
          return;
        }
        window._myProductsFetch = now;

        const res = await fetch(`https://api.minymol.com/products/by-provider/${user.proveedorInfo.id}`);

        if (!res.ok) {
          if (res.status === 429) {
            setLoading(false);
            return;
          }
          throw new Error(`HTTP ${res.status}`);
        }

        const data = await res.json();
        const ids = data.map(p => p.product_id).filter(Boolean);

        if (!ids.length) {
          setProductos([]);
          setFilteredProducts([]);
          return;
        }

        const previewsRes = await fetch('https://api.minymol.com/products/previews', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ids })
        });

        if (!previewsRes.ok) {
          if (previewsRes.status === 429) {
            setLoading(false);
            return;
          }
          throw new Error(`HTTP ${previewsRes.status}`);
        }

        const previews = await previewsRes.json();
        setProductos(previews);
        setFilteredProducts(previews);
      } catch (err) {
        if (!err.message?.includes('429')) {
          console.error("Error al obtener productos del proveedor:", err);
        }
        setProductos([]);
        setFilteredProducts([]);
      } finally {
        setLoading(false);
      }
    };

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        fetchProductos();
      } else {
        console.log('❌ No hay usuario autenticado, redirigiendo a login');
        setLoading(false);
        navigate(getLoginRoute());
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (!loading && filteredProducts.length > 0) {
      if (viewMode === 'table') {
        setVisibleProducts(filteredProducts);
        setHasMore(false);
      } else {
        const inicial = filteredProducts.slice(0, ITEMS_PER_LOAD);
        setVisibleProducts(inicial);
        loadIndexRef.current = ITEMS_PER_LOAD;
        setHasMore(filteredProducts.length > ITEMS_PER_LOAD);
      }
    } else if (!loading && filteredProducts.length === 0) {
      setVisibleProducts([]);
      setHasMore(false);
    }
  }, [loading, filteredProducts, viewMode]);

  useEffect(() => {
    const throttledScroll = throttle(handleScroll, 200);
    window.addEventListener('scroll', throttledScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', throttledScroll);
      throttledScroll.cancel();
    };
  }, [handleScroll]);

  const addProduct = () => {
    navigate("/add-product");
  };

  // Filtrar productos por búsqueda y categoría
  const applyFilters = useCallback(() => {
    let filtered = [...productos];

    // Filtro por búsqueda
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(product => {
        const name = product.name?.toLowerCase() || '';
        const provider = product.provider?.toLowerCase() || '';
        const subCategory = product.subCategory?.name?.toLowerCase() || '';
        return name.includes(searchLower) || provider.includes(searchLower) || subCategory.includes(searchLower);
      });
    }

    // Filtro por categoría (solo en modo embebido)
    if (embedded && selectedCategory && selectedCategory !== 'all') {
      filtered = filtered.filter(product => {
        const categoryId = product.subCategory?.id || product.category?.id;
        return categoryId === parseInt(selectedCategory);
      });
    }

    setFilteredProducts(filtered);
  }, [productos, searchTerm, selectedCategory, embedded]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const handleSearchInputChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // eslint-disable-next-line no-unused-vars
  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
  };

  // Close bulk menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (bulkMenuRef.current && !bulkMenuRef.current.contains(event.target)) {
        setShowBulkMenu(false);
      }
    };

    if (showBulkMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showBulkMenu]);

  const handleImportComplete = () => {
    // Refresh products after import
    window.location.reload();
  };

  const handleEditComplete = () => {
    // Refresh products after edit
    window.location.reload();
  };

  return (
    <div className="my-products-container">
      {!embedded && (
        <Header
          minimal={true}
          providerName={user?.proveedorInfo?.nombre_empresa}
          providerLogo={user?.proveedorInfo?.logo_url}
          menuProvider={true}
          currentPage='products'
        />
      )}

      {embedded && <EmbeddedLayout />}

      {/* Toolbar - SOLO EN MODO EMBEBIDO */}
      {!loading && embedded && (
        <div className="my-products-toolbar">
          {/* Barra de búsqueda */}
          <input
            type="text"
            placeholder="Buscar productos..."
            value={searchTerm}
            onChange={handleSearchInputChange}
            className="my-products-search"
          />

          {/* Botón crear producto */}
          <button
            onClick={addProduct}
            className="my-products-add-btn"
            title="Agregar producto"
          >
            <FontAwesomeIcon icon={faPlus} />
            <span>Crear producto</span>
          </button>

          {/* Toggle vista tarjetas/lista */}
          <div className="my-products-view-toggle">
            <button
              className={`view-toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
              title="Vista de tarjetas"
            >
              <FontAwesomeIcon icon={faGrip} />
            </button>
            <button
              className={`view-toggle-btn ${viewMode === 'table' ? 'active' : ''}`}
              onClick={() => setViewMode('table')}
              title="Vista de tabla"
            >
              <FontAwesomeIcon icon={faList} />
            </button>
          </div>

          {/* Menú de opciones masivas */}
          <div className="my-products-bulk-menu-container" ref={bulkMenuRef}>
            <button
              className="my-products-bulk-menu-btn"
              onClick={() => setShowBulkMenu(!showBulkMenu)}
              title="Opciones masivas"
            >
              <FontAwesomeIcon icon={faEllipsisV} />
            </button>

            {showBulkMenu && (
              <div className="my-products-bulk-dropdown">
                <button
                  onClick={() => {
                    setShowImportModal(true);
                    setShowBulkMenu(false);
                  }}
                  className="my-products-bulk-dropdown-item"
                >
                  <FontAwesomeIcon icon={faPlus} />
                  <span>Importar productos</span>
                </button>
                <button
                  onClick={() => {
                    setShowEditModal(true);
                    setShowBulkMenu(false);
                  }}
                  className="my-products-bulk-dropdown-item"
                >
                  <FontAwesomeIcon icon={faList} />
                  <span>Editar masivamente</span>
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Buscador simple - SOLO EN MODO NO EMBEBIDO */}
      {!loading && !embedded && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          padding: '20px',
          gap: '10px',
          maxWidth: '600px',
          margin: '0 auto'
        }}>
          <input
            type="text"
            placeholder="Buscar por nombre, proveedor o categoría..."
            value={searchTerm}
            onChange={handleSearchInputChange}
            style={{
              flex: 1,
              padding: '12px 16px',
              fontSize: '16px',
              border: '2px solid #e0e0e0',
              borderRadius: '8px',
              outline: 'none',
              transition: 'border-color 0.3s',
            }}
            onFocus={(e) => e.target.style.borderColor = '#fa7e17'}
            onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              style={{
                padding: '12px 24px',
                fontSize: '16px',
                backgroundColor: '#fa7e17',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'background-color 0.3s',
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#e56d0f'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#fa7e17'}
            >
              Limpiar
            </button>
          )}
        </div>
      )}

      {loading ? (
        <div className="my-products-loader">
          <ClipLoader color={embedded ? "#4169E1" : "#fa7e17"} size={40} />
        </div>
      ) : (
        <>
          {/* MODO EMBEBIDO - Con vista grid/tabla */}
          {embedded ? (
            <>
              {viewMode === 'grid' ? (
                <>
                  <Masonry
                    breakpointCols={breakpointColumnsObj}
                    className="products-cont masonry-grid"
                    columnClassName="masonry-grid_column"
                  >
                    {visibleProducts.map((prod, index) => (
                      <ProductWrapper 
                        key={`wrapper-${index}`} 
                        product={prod} 
                        index={index}
                        disableClick={true}
                      />
                    ))}
                  </Masonry>
                  {loadingMore && hasMore && (
                    <div className="loader-bottom">
                      <ClipLoader color="#4169E1" size={25} />
                    </div>
                  )}
                  {!hasMore && visibleProducts.length > 0 && (
                    <div className="my-products-end-message">
                      No hay más productos para mostrar
                    </div>
                  )}
                </>
              ) : (
                <ProductTable products={visibleProducts} isEmbedded={true} />
              )}

              {visibleProducts.length === 0 && (
                <div className="my-products-empty">
                  <p>No se encontraron productos</p>
                </div>
              )}
            </>
          ) : (
            /* MODO NORMAL - Vista grid tradicional */
            <>
              <Masonry
                breakpointCols={breakpointColumnsObj}
                className="products-cont masonry-grid"
                columnClassName="masonry-grid_column"
              >
                <div className='products-add' onClick={addProduct}>
                  <div className='add-button'>
                    <FontAwesomeIcon icon={faPlus} />
                  </div>
                </div>
                {visibleProducts.map((prod, index) => (
                  <ProductWrapper 
                    key={`wrapper-${index}`} 
                    product={prod} 
                    index={index}
                    disableClick={false}
                  />
                ))}
              </Masonry>
              {loadingMore && hasMore && (
                <div className="loader-bottom">
                  <ClipLoader color="#fa7e17" size={25} />
                </div>
              )}
              {!hasMore && visibleProducts.length > 0 && (
                <div style={{ textAlign: 'center', color: '#888', padding: '1rem' }}>
                  No hay más productos para mostrar
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Botón flotante para agregar producto - SOLO EN MODO EMBEBIDO (removido, ahora está en toolbar) */}

      {!embedded && visibleProducts.length > 0 && (
        <ShareButton
          title={`Catálogo de ${user?.proveedorInfo?.nombre_empresa}`}
          imageUrl={user?.proveedorInfo?.logo_url || 'https://cdn.minymol.com/uploads/logoblanco.webp'}
          isCatalogo={true}
        />
      )}

      {!embedded && <NavInf />}
      {!embedded && <Footer />}

      {/* Modales de importación y edición masiva */}
      <ProductBulkImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImportComplete={handleImportComplete}
      />
      <ProductBulkEditModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onEditComplete={handleEditComplete}
      />
    </div>
  );
}

export default MyProducts;
